import { Link } from "react-router";
import type { GroupScheduleView } from "@edu/shared";
import { CopyLink } from "../shell/CopyLink.tsx";
import { OPEN_GROUP_SCHEDULE, scheduleSkippedLines } from "./group-text.ts";

/**
 * Что окно создания занятия показывает В ОТВЕТ на нажатие.
 *
 * Отдельным файлом, потому что таких ответов стало три, и ни один из них не
 * выбор адресата: выданная ссылка нового ученика, те же ссылки после заведения
 * занятия и список невзятых расписанием. Жили они в `OccurrenceRecipient.tsx`,
 * и с приходом ссылок тот перевалил за четыреста строк ровно на них.
 *
 * ⚠️ Общее у двух последних одно, и ради него они и стоят парой: **форма
 * уступает им место, а не показывается под ними**. Оставленная над ответом
 * кнопка «Завести» завела бы второе занятие тем же нажатием, а человек в этот
 * момент читает ответ, а не форму.
 */

/** Ссылка заведённого ученика вместе с именем, которому её отдавать. */
export interface InviteLink {
  name: string;
  url: string;
  /** Ссылку забрали: нажали кнопку либо скопировали выделением из поля. */
  copied: boolean;
}

/**
 * Что будет со ссылкой, если её не забрать сейчас.
 *
 * ⚠️ Названо ровно то, чего человек знать не может, и ничего сверх. «Скопируйте
 * её сейчас» отсюда убрано: кнопка копирования стоит строкой ниже и говорит это
 * сама, а слово рядом с фигурой того же смысла это ровно то, что снимает раздел
 * 8б `docs/cabinet-tables-design.md`.
 *
 * ⚠️ Про то, что перевыпуск ломает старую ссылку, здесь не сказано НАМЕРЕННО, и
 * это не умолчание. В эту секунду старой ссылки нет ни у кого: её выпустили
 * несколько секунд назад и никому не отдали. Предупреждение о последствии,
 * которое сейчас невозможно, читается угрозой и делает дешёвое восстановление
 * страшным. Полная цена перевыпуска стоит там, где его нажимают, в карточке
 * ученика (`features/students/StudentAccess.tsx`).
 *
 * Строкой, а не разметкой: её читают в двух местах окна, до заведения занятия и
 * после, и разъехавшись, две копии обещали бы разное.
 */
const INVITE_ONCE =
  "Второй раз мы её не покажем. Если ссылка потеряется, новую можно выпустить в разделе «Ученики».";

/**
 * Одна выданная ссылка: чья она, поле с адресом и что с ней будет дальше.
 *
 * Показывается ровно один раз: в базе только SHA-256, повторно сервер её не
 * отдаст. Отсюда отдельный блок, который не исчезает до закрытия окна, и отсюда
 * же второе место показа, `InviteHandoff` ниже: заведение занятия окно
 * закрывает, и ссылка уезжала вместе с ним.
 *
 * ⚠️ **Кнопка копирования здесь обязательна, а не удобна.** До 03.09.2026
 * ссылка стояла голым текстом (`mono-break`), то есть забрать её можно было
 * только выделением, и выделение отменялось промахом мыши. Кнопка при этом со
 * СЛОВОМ, а не значком: слово убирается там, где рядом уже стоит слово, а этот
 * блок человек читает один раз на ученика, и ребус в нём стоит дороже
 * сэкономленного места (раздел 8б `docs/cabinet-tables-design.md`).
 *
 * ⚠️ Имя стоит зачином, а не внутри фразы, и существительного перед ним нет
 * вовсе. «Ученику «Аня» осталось отдать» не произносится вслух (падеж снаружи
 * кавычек, именительный внутри) и вдобавок зовёт девочку учеником: род у имени
 * нам неизвестен, а склонять чужое имя мы не умеем.
 */
export function InviteBlock({
  invite,
  onCopied,
}: {
  invite: InviteLink;
  onCopied: (url: string) => void;
}) {
  return (
    <div className="note">
      <p>«{invite.name}»: постоянная ссылка на все занятия.</p>
      <CopyLink url={invite.url} onCopied={() => onCopied(invite.url)} />
      <p>{INVITE_ONCE}</p>
    </div>
  );
}

/**
 * Ссылки учеников, заведённых в этом окне, когда занятие уже создано.
 *
 * ⚠️ **Существует потому, что окно закрывалось само.** Успешное создание звало
 * `onClose`, и незабранная ссылка исчезала вместе с формой: молча, в тот самый
 * момент, когда человек радуется получившемуся занятию. Показать её второй раз
 * сервер не может, поэтому окно на этом шаге остаётся.
 *
 * Показываются ВСЕ ссылки окна, включая уже забранные: в буфере обмена лежит
 * последняя, и человеку, заведшему двоих, вторая нужна не меньше первой.
 */
export function InviteHandoff({
  invites,
  onCopied,
}: {
  invites: InviteLink[];
  onCopied: (url: string) => void;
}) {
  return (
    <>
      {/*
        Слово нейтральное намеренно: этой же веткой приходит и заведённое
        расписание группы, и назвать его занятием значило бы соврать. Что именно
        завелось, человек только что выбрал сам кнопкой.
      */}
      <p>Готово.</p>
      {invites.map((one) => (
        <InviteBlock key={one.url} invite={one} onCopied={onCopied} />
      ))}
    </>
  );
}

/**
 * Кого расписание не застало. Показывается ДО закрытия диалога.
 *
 * ⚠️ **Из группового расписания никто не выпадает молча** (5.5 CLAUDE.md).
 * Отвязанная пара новых договорённостей не заводит, а из группы отвязка не
 * выкидывает, то есть человек остаётся в составе и остаётся без занятий. Имена
 * приезжают из карты, собранной при загрузке, а не вторым запросом.
 *
 * Ссылка ведёт туда, где заведённое расписание видно целиком: она же и есть
 * подтверждение, что оно завелось, потому что открывать нечего, когда ничего не
 * вышло.
 */
export function ScheduleSkipped({
  view,
  nameOf,
}: {
  view: GroupScheduleView;
  nameOf: (userId: string) => string | undefined;
}) {
  return (
    <div className="note">
      {scheduleSkippedLines(view.skipped, nameOf).map((line) => (
        <p key={line}>{line}</p>
      ))}
      <p>
        <Link className="btn" to={`/schedule/groups/${view.groupId}`}>
          {OPEN_GROUP_SCHEDULE}
        </Link>
      </p>
    </div>
  );
}
